import React, { useCallback, useEffect, useRef, useState } from 'react';
import adminApi from '../api/client';

type OfficialType = 'author' | 'publisher';

interface Candidate {
  id: string;
  display_name: string;
  name_sinhala?: string | null;
}

interface CandidateResponse {
  official_type: OfficialType;
  items: Candidate[];
  total: number;
  page: number;
  per_page: number;
}

interface ProvisionResponse {
  club_id: string;
  official_type: OfficialType;
  subject_id: string;
  state: 'draft';
}

type CircleState = 'draft' | 'published' | 'paused' | 'retired';
type LifecycleTargetState = 'draft' | 'published' | 'paused' | 'retired';

interface OfficialCircle {
  club_id: string;
  official_type: OfficialType;
  subject_id: string;
  display_name: string;
  name_sinhala?: string | null;
  state: CircleState;
  state_version: number;
  club_is_active: boolean;
  club_is_private: boolean;
}

interface OfficialCircleListResponse {
  items: OfficialCircle[];
  total: number;
  page: number;
  per_page: number;
}

interface LifecycleResponse {
  club_id: string;
  official_type: OfficialType;
  subject_id: string;
  state: CircleState;
  state_version: number;
  profile_version?: number;
}

interface LifecycleAction {
  circle: OfficialCircle;
  targetState: LifecycleTargetState;
  preflight?: PublicationPreflight;
}

interface PublicationPreflight {
  club_id: string;
  state: CircleState;
  state_version: number;
  profile_version: number | null;
  stage_ready: boolean;
  release_gate_enabled: boolean;
  profile_ready: boolean;
  canonical_subject_eligible: boolean;
  backing_club_eligible: boolean;
  missing_requirements: string[];
}

interface StagedPublicationStatus {
  state_version: number;
  profile_version: number;
  release_gate_enabled: boolean;
}

type ProfileApprovalStatus = 'approved' | 'not_approved';
type ProfileAction = 'approve' | 'revoke';

interface OfficialCircleProfile {
  club_id: string;
  profile_exists: boolean;
  public_introduction: string | null;
  profile_version: number | null;
  approval_status: ProfileApprovalStatus;
  approved_at: string | null;
  ready: boolean;
  missing_requirements: string[];
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
    };
  };
}

const PAGE_SIZE = 20;
const MAX_REASON_LENGTH = 1000;

function getStatus(error: unknown): number | undefined {
  return (error as ApiErrorShape).response?.status;
}

function getValidationDetail(detail: unknown): string | null {
  if (!Array.isArray(detail)) return null;
  const messages = detail
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const message = (item as { msg?: unknown }).msg;
      return typeof message === 'string' ? message : null;
    })
    .filter((message): message is string => Boolean(message));
  return messages.length ? messages.join(' ') : null;
}

function getSafeErrorMessage(error: unknown): string {
  const status = getStatus(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to provision an Official Circle.';
  if (status === 404) return 'The selected canonical subject is no longer available.';
  if (status === 409) return 'This canonical subject already has an Official Circle or another provisioning is in progress. No second Circle was created.';
  if (status === 422) {
    const detail = getValidationDetail((error as ApiErrorShape).response?.data?.detail);
    return detail || 'The provisioning request did not pass validation.';
  }
  if (status === 500) return 'Official Circle provisioning failed and was rolled back.';
  if (status === undefined) return 'Outcome unknown — do not retry; verify with an admin.';
  return 'The Official Circle request could not be completed.';
}

function getSafeLifecycleErrorMessage(error: unknown): string {
  const status = getStatus(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to change Official Circle state.';
  if (status === 404) return 'This Official Circle is no longer available. Refresh the list.';
  if (status === 409) return 'State changed; refresh required.';
  if (status === 422) {
    const detail = getValidationDetail((error as ApiErrorShape).response?.data?.detail);
    return detail || 'Check the lifecycle reason and try again.';
  }
  if (status === 500) return 'Official Circle lifecycle change failed and was rolled back.';
  if (status === undefined) {
    return 'Outcome unknown; do not retry; refresh with an admin before taking another action.';
  }
  return 'The Official Circle lifecycle change could not be completed.';
}

function getSafePreflightErrorMessage(error: unknown): string {
  const status = getStatus(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to inspect publication readiness.';
  if (status === 404) return 'This Official Circle is no longer available. Refresh the list.';
  if (status === 422) return 'The publication preflight request did not pass validation.';
  if (status === 500) return 'Publication readiness could not be checked safely.';
  if (status === undefined) return 'Outcome unknown; do not retry; refresh with an admin.';
  return 'Publication readiness could not be checked safely.';
}

function getSafeProfileErrorMessage(error: unknown, operation: 'load' | 'save' | 'approval'): string {
  const status = getStatus(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return operation === 'load'
    ? 'This admin session is not permitted to view Official Circle profiles.'
    : 'This admin session is not permitted to change Official Circle profiles.';
  if (status === 404) return operation === 'load'
    ? 'This Official Circle profile is no longer available. Refresh the list.'
    : 'This Official Circle is no longer available. Refresh the list.';
  if (status === 409) return 'Profile changed; refresh required before taking another action.';
  if (status === 422) {
    const detail = getValidationDetail((error as ApiErrorShape).response?.data?.detail);
    return detail || (operation === 'approval'
      ? 'The profile approval request did not pass validation.'
      : 'The profile introduction did not pass validation.');
  }
  if (status === 500) {
    if (operation === 'load') return 'Unable to load Official Circle profile safely.';
    return operation === 'approval'
      ? 'Official Circle profile approval change failed and was rolled back.'
      : 'Official Circle profile change failed and was rolled back.';
  }
  if (status === undefined) {
    return 'Outcome unknown; do not retry; refresh with an admin before taking another action.';
  }
  return 'The Official Circle profile request could not be completed.';
}

function getSafeListErrorMessage(error: unknown): string {
  const status = getStatus(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to view Official Circles.';
  return 'Unable to load Official Circles safely.';
}

const OfficialCircles: React.FC = () => {
  const [officialType, setOfficialType] = useState<OfficialType>('author');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ProvisionResponse | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const requestSequence = useRef(0);
  const [circles, setCircles] = useState<OfficialCircle[]>([]);
  const [circleTotal, setCircleTotal] = useState(0);
  const [circlePage, setCirclePage] = useState(1);
  const [circleStateFilter, setCircleStateFilter] = useState<CircleState | ''>('');
  const [circleTypeFilter, setCircleTypeFilter] = useState<OfficialType | ''>('');
  const [loadingCircles, setLoadingCircles] = useState(true);
  const [circleError, setCircleError] = useState<string | null>(null);
  const [lifecycleAction, setLifecycleAction] = useState<LifecycleAction | null>(null);
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);
  const [publicationPreflight, setPublicationPreflight] = useState<Record<string, PublicationPreflight>>({});
  const [stagedPublication, setStagedPublication] = useState<Record<string, StagedPublicationStatus>>({});
  const [preflightLoadingId, setPreflightLoadingId] = useState<string | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const circleRequestSequence = useRef(0);
  const [profileCircle, setProfileCircle] = useState<OfficialCircle | null>(null);
  const [profile, setProfile] = useState<OfficialCircleProfile | null>(null);
  const [profileIntroduction, setProfileIntroduction] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileAction, setProfileAction] = useState<ProfileAction | null>(null);
  const [profileReason, setProfileReason] = useState('');
  const [profileActionConfirmed, setProfileActionConfirmed] = useState(false);
  const profileRequestSequence = useRef(0);

  const fetchCircles = useCallback(async () => {
    const sequence = ++circleRequestSequence.current;
    setLoadingCircles(true);
    setCircleError(null);

    const params: Record<string, string | number> = {
      page: circlePage,
      per_page: PAGE_SIZE,
    };
    if (circleStateFilter) params.state = circleStateFilter;
    if (circleTypeFilter) params.official_type = circleTypeFilter;

    try {
      const response = await adminApi.get<OfficialCircleListResponse>(
        '/admin/official-circles',
        { params },
      );
      if (sequence !== circleRequestSequence.current) return;
      setCircles(response.data.items || []);
      setCircleTotal(response.data.total || 0);
    } catch (error) {
      if (sequence !== circleRequestSequence.current) return;
      setCircleError(getSafeListErrorMessage(error));
    } finally {
      if (sequence === circleRequestSequence.current) setLoadingCircles(false);
    }
  }, [circlePage, circleStateFilter, circleTypeFilter]);

  useEffect(() => {
    void fetchCircles();
  }, [fetchCircles]);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const timer = window.setTimeout(async () => {
      setLoadingCandidates(true);
      setCandidateError(null);
      try {
        const params: Record<string, string | number> = {
          official_type: officialType,
          page,
          per_page: PAGE_SIZE,
        };
        const trimmedSearch = search.trim();
        if (trimmedSearch) params.q = trimmedSearch;

        const response = await adminApi.get<CandidateResponse>(
          '/admin/official-circles/candidates',
          { params },
        );
        if (sequence !== requestSequence.current) return;
        setCandidates(response.data.items || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        if (sequence !== requestSequence.current) return;
        setCandidateError(
          getStatus(error) === 401
            ? 'Your admin session expired. Please sign in again.'
            : 'Unable to load eligible canonical subjects.',
        );
      } finally {
        if (sequence === requestSequence.current) setLoadingCandidates(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [officialType, page, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const trimmedReason = reason.trim();
  const canSubmit = Boolean(selected && trimmedReason && confirmed && !submitting);

  const changeType = (nextType: OfficialType) => {
    if (nextType === officialType) return;
    setOfficialType(nextType);
    setSearch('');
    setPage(1);
    setSelected(null);
    setResult(null);
    setActionError(null);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setSelected(null);
    setResult(null);
    setActionError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || !trimmedReason || !confirmed || submitting) return;

    setSubmitting(true);
    setActionError(null);
    setResult(null);

    const payload = {
      official_type: officialType,
      author_id: officialType === 'author' ? selected.id : null,
      publisher_id: officialType === 'publisher' ? selected.id : null,
      reason: trimmedReason,
    };

    try {
      const response = await adminApi.post<ProvisionResponse>(
        '/admin/official-circles',
        payload,
      );
      if (response.status !== 201) {
        throw new Error('Unexpected provisioning response');
      }
      setResult(response.data);
      setSelected(null);
      setReason('');
      setConfirmed(false);
      setPage(1);
      void fetchCircles();
    } catch (error) {
      setActionError(getSafeErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const circleTotalPages = Math.max(1, Math.ceil(circleTotal / PAGE_SIZE));
  const profilePublished = profileCircle?.state === 'published';
  const profileIntroductionDirty = Boolean(
    profile
    && profileIntroduction.trim() !== (profile.public_introduction || '').trim(),
  );
  const lifecyclePublishReady = Boolean(
    lifecycleAction?.targetState !== 'published'
    || (
      lifecycleAction.preflight?.stage_ready
      && lifecycleAction.preflight.state_version === lifecycleAction.circle.state_version
      && lifecycleAction.preflight.profile_version !== null
    )
  );
  const canSubmitLifecycle = Boolean(
    lifecycleAction
    && lifecycleReason.trim()
    && lifecyclePublishReady
    && !lifecycleSubmitting,
  );

  const openLifecycleAction = (
    circle: OfficialCircle,
    targetState: LifecycleTargetState,
    preflight?: PublicationPreflight,
  ) => {
    setLifecycleAction({ circle, targetState, preflight });
    setLifecycleReason('');
    setLifecycleError(null);
  };

  const loadPublicationPreflight = async (circle: OfficialCircle) => {
    if (preflightLoadingId === circle.club_id) return;
    setPreflightLoadingId(circle.club_id);
    setPreflightError(null);
    try {
      const response = await adminApi.get<PublicationPreflight>(
        `/admin/official-circles/${encodeURIComponent(circle.club_id)}/publication-preflight`,
      );
      if (response.status !== 200) {
        throw new Error('Unexpected publication preflight response');
      }
      setPublicationPreflight((current) => ({
        ...current,
        [circle.club_id]: response.data,
      }));
    } catch (error) {
      setPreflightError(getSafePreflightErrorMessage(error));
    } finally {
      setPreflightLoadingId(null);
    }
  };

  const closeLifecycleAction = () => {
    if (lifecycleSubmitting) return;
    setLifecycleAction(null);
    setLifecycleReason('');
    setLifecycleError(null);
  };

  const handleLifecycleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!lifecycleAction || !lifecycleReason.trim() || !lifecyclePublishReady || lifecycleSubmitting) return;

    const reason = lifecycleReason.trim();
    if (reason.length > MAX_REASON_LENGTH) {
      setLifecycleError('Lifecycle reason must be 1000 characters or fewer.');
      return;
    }

    setLifecycleSubmitting(true);
    setLifecycleError(null);

    try {
      const payload: {
        target_state: LifecycleTargetState;
        expected_state_version: number;
        expected_profile_version?: number;
        reason: string;
      } = {
        target_state: lifecycleAction.targetState,
        expected_state_version: lifecycleAction.circle.state_version,
        reason,
      };
      if (lifecycleAction.targetState === 'published') {
        const profileVersion = lifecycleAction.preflight?.profile_version;
        if (profileVersion === null || profileVersion === undefined) {
          setLifecycleError('Publication readiness is stale; check it again before publishing.');
          return;
        }
        payload.expected_profile_version = profileVersion;
      }
      const response = await adminApi.patch<LifecycleResponse>(
        `/admin/official-circles/${encodeURIComponent(lifecycleAction.circle.club_id)}/lifecycle`,
        payload,
      );
      if (response.status !== 200) {
        setLifecycleError('The lifecycle response was not accepted. Refresh with an admin.');
        return;
      }

      setCircles((current) => current.map((circle) => (
        circle.club_id === response.data.club_id
          ? {
              ...circle,
              state: response.data.state,
              state_version: response.data.state_version,
              club_is_active: response.data.state === 'published',
              club_is_private: response.data.state !== 'published',
            }
          : circle
      )));
      setProfileCircle((current) => (
        current && current.club_id === response.data.club_id
          ? {
              ...current,
              state: response.data.state,
              state_version: response.data.state_version,
            }
          : current
      ));
      setPublicationPreflight((current) => {
        const next = { ...current };
        delete next[response.data.club_id];
        return next;
      });
      setStagedPublication((current) => {
        const next = { ...current };
        if (
          response.data.state === 'published'
          && lifecycleAction.preflight?.profile_version !== null
          && lifecycleAction.preflight?.profile_version !== undefined
        ) {
          next[response.data.club_id] = {
            state_version: response.data.state_version,
            profile_version: lifecycleAction.preflight.profile_version,
            release_gate_enabled: Boolean(lifecycleAction.preflight.release_gate_enabled),
          };
        } else {
          delete next[response.data.club_id];
        }
        return next;
      });
      setLifecycleAction(null);
      setLifecycleReason('');
    } catch (error) {
      setLifecycleError(getSafeLifecycleErrorMessage(error));
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  const closeProfile = () => {
    if (profileSubmitting) return;
    setProfileCircle(null);
    setProfile(null);
    setProfileIntroduction('');
    setProfileError(null);
    setProfileAction(null);
    setProfileReason('');
    setProfileActionConfirmed(false);
  };

  const openProfile = async (circle: OfficialCircle) => {
    const sequence = ++profileRequestSequence.current;
    setProfileCircle(circle);
    setProfile(null);
    setProfileIntroduction('');
    setProfileError(null);
    setProfileAction(null);
    setProfileReason('');
    setProfileActionConfirmed(false);
    setProfileLoading(true);
    try {
      const response = await adminApi.get<OfficialCircleProfile>(
        `/admin/official-circles/${encodeURIComponent(circle.club_id)}/profile`,
      );
      if (sequence !== profileRequestSequence.current) return;
      setProfile(response.data);
      setProfileIntroduction(response.data.public_introduction || '');
    } catch (error) {
      if (sequence !== profileRequestSequence.current) return;
      setProfileError(getSafeProfileErrorMessage(error, 'load'));
    } finally {
      if (sequence === profileRequestSequence.current) setProfileLoading(false);
    }
  };

  const handleProfileSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profileCircle || !profile || profilePublished || profileSubmitting) return;
    const introduction = profileIntroduction.trim();
    if (!introduction || introduction.length > 2000) {
      setProfileError('Profile introduction must be between 1 and 2000 characters after trimming.');
      return;
    }

    setProfileSubmitting(true);
    setProfileError(null);
    try {
      const response = await adminApi.patch<OfficialCircleProfile>(
        `/admin/official-circles/${encodeURIComponent(profileCircle.club_id)}/profile`,
        {
          public_introduction: introduction,
          expected_profile_version: profile.profile_version ?? 0,
        },
      );
      if (response.status !== 200) {
        setProfileError('The profile response was not accepted. Refresh with an admin.');
        return;
      }
      setProfile(response.data);
      setProfileIntroduction(response.data.public_introduction || '');
    } catch (error) {
      setProfileError(getSafeProfileErrorMessage(error, 'save'));
    } finally {
      setProfileSubmitting(false);
    }
  };

  const openProfileApproval = (action: ProfileAction) => {
    if (profilePublished) return;
    setProfileAction(action);
    setProfileReason('');
    setProfileActionConfirmed(false);
    setProfileError(null);
  };

  const handleProfileApproval = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !profileCircle
      || !profile
      || !profileAction
      || !profileActionConfirmed
      || !profileReason.trim()
      || profilePublished
      || profileSubmitting
    ) return;
    const reason = profileReason.trim();
    if (reason.length > MAX_REASON_LENGTH) {
      setProfileError('Approval or revocation reason must be 1000 characters or fewer.');
      return;
    }

    setProfileSubmitting(true);
    setProfileError(null);
    try {
      const response = await adminApi.patch<OfficialCircleProfile>(
        `/admin/official-circles/${encodeURIComponent(profileCircle.club_id)}/profile/approval`,
        {
          action: profileAction,
          expected_profile_version: profile.profile_version,
          reason,
        },
      );
      if (response.status !== 200) {
        setProfileError('The approval response was not accepted. Refresh with an admin.');
        return;
      }
      setProfile(response.data);
      setProfileIntroduction(response.data.public_introduction || '');
      setProfileAction(null);
      setProfileReason('');
      setProfileActionConfirmed(false);
    } catch (error) {
      setProfileError(getSafeProfileErrorMessage(error, 'approval'));
    } finally {
      setProfileSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1EFE8] p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6A38]">Controlled admin action</p>
          <h1 className="text-3xl font-bold text-[#085041] mt-2">Official Circles</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Provision one draft Circle from an existing canonical Author or Publisher identity.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-800">This action creates a draft only</p>
          <p className="text-sm text-gray-600 mt-2">
            The Club container will be private and inactive, with zero members and no host grant.
            Nothing will be published or made publicly discoverable.
          </p>
        </div>

        <section aria-labelledby="existing-official-circles-heading" className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 id="existing-official-circles-heading" className="text-xl font-semibold text-gray-800">Existing Official Circles</h2>
              <p className="text-sm text-gray-500 mt-1">Review the current lifecycle state of official Circle bindings.</p>
            </div>
            <button
              type="button"
              onClick={() => { void fetchCircles(); }}
              disabled={loadingCircles || lifecycleSubmitting}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:border-[#085041] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Refresh list
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm font-medium text-gray-700">
              State
              <select
                aria-label="Official Circle state filter"
                value={circleStateFilter}
                onChange={(event) => {
                  setCircleStateFilter(event.target.value as CircleState | '');
                  setCirclePage(1);
                }}
                disabled={lifecycleSubmitting}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              >
                <option value="">All states</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="paused">Paused</option>
                <option value="retired">Retired</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Official type
              <select
                aria-label="Official Circle type filter"
                value={circleTypeFilter}
                onChange={(event) => {
                  setCircleTypeFilter(event.target.value as OfficialType | '');
                  setCirclePage(1);
                }}
                disabled={lifecycleSubmitting}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              >
                <option value="">All types</option>
                <option value="author">Author</option>
                <option value="publisher">Publisher</option>
              </select>
            </label>
          </div>

          {loadingCircles && (
            <p className="rounded-lg border border-gray-200 px-4 py-5 text-sm text-gray-500">Loading Official Circles…</p>
          )}
          {!loadingCircles && circleError && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {circleError}
            </div>
          )}
          {preflightError && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {preflightError}
            </div>
          )}
          {!loadingCircles && !circleError && circles.length === 0 && (
            <p className="rounded-lg border border-gray-200 px-4 py-5 text-sm text-gray-500">No Official Circles found.</p>
          )}
          {!loadingCircles && !circleError && circles.length > 0 && (
            <div className="space-y-3">
              {circles.map((circle) => {
                const preflight = publicationPreflight[circle.club_id];
                return (
                <article key={circle.club_id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-800">{circle.display_name}</h3>
                      {circle.name_sinhala && <p className="text-sm text-gray-500 mt-0.5">{circle.name_sinhala}</p>}
                      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div><dt className="inline text-gray-500">Circle ID: </dt><dd className="inline text-gray-800 break-all">{circle.club_id}</dd></div>
                        <div><dt className="inline text-gray-500">Type: </dt><dd className="inline text-gray-800">{circle.official_type}</dd></div>
                        <div><dt className="inline text-gray-500">Subject ID: </dt><dd className="inline text-gray-800 break-all">{circle.subject_id}</dd></div>
                        <div><dt className="inline text-gray-500">State: </dt><dd className="inline text-gray-800">{circle.state}</dd></div>
                        <div><dt className="inline text-gray-500">State version: </dt><dd className="inline text-gray-800">{circle.state_version}</dd></div>
                        <div><dt className="inline text-gray-500">Club active: </dt><dd className="inline text-gray-800">{circle.club_is_active ? 'Yes' : 'No'}</dd></div>
                        <div><dt className="inline text-gray-500">Club private: </dt><dd className="inline text-gray-800">{circle.club_is_private ? 'Yes' : 'No'}</dd></div>
                      </dl>
                    </div>
                    <div className="shrink-0 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => { void openProfile(circle); }}
                        disabled={profileSubmitting}
                        className="rounded-lg border border-[#085041] px-3 py-2 text-sm font-semibold text-[#085041] hover:bg-[#085041] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Profile
                      </button>
                      {(circle.state === 'draft' || circle.state === 'paused') && (
                        <>
                          <button
                            type="button"
                            onClick={() => { void loadPublicationPreflight(circle); }}
                            disabled={preflightLoadingId === circle.club_id || lifecycleSubmitting}
                            className="rounded-lg border border-[#8A6A38] px-3 py-2 text-sm font-semibold text-[#8A6A38] hover:bg-[#8A6A38] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {preflightLoadingId === circle.club_id ? 'Checking…' : 'Check publication readiness'}
                          </button>
                          {preflight?.stage_ready && (
                            <button
                              type="button"
                              onClick={() => openLifecycleAction(circle, 'published', preflight)}
                              disabled={lifecycleSubmitting}
                              className="rounded-lg border border-[#085041] px-3 py-2 text-sm font-semibold text-[#085041] hover:bg-[#085041] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Publish
                            </button>
                          )}
                        </>
                      )}
                      {circle.state === 'published' && (
                        <>
                          <button
                            type="button"
                            onClick={() => openLifecycleAction(circle, 'paused')}
                            disabled={lifecycleSubmitting}
                            className="rounded-lg border border-[#8A6A38] px-3 py-2 text-sm font-semibold text-[#8A6A38] hover:bg-[#8A6A38] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Pause
                          </button>
                          <button
                            type="button"
                            onClick={() => openLifecycleAction(circle, 'retired')}
                            disabled={lifecycleSubmitting}
                            className="rounded-lg border border-[#D85A30] px-3 py-2 text-sm font-semibold text-[#D85A30] hover:bg-[#D85A30] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Retire
                          </button>
                        </>
                      )}
                      {circle.state === 'draft' && (
                        <button
                          type="button"
                          onClick={() => openLifecycleAction(circle, 'retired')}
                          disabled={lifecycleSubmitting}
                          className="rounded-lg border border-[#D85A30] px-3 py-2 text-sm font-semibold text-[#D85A30] hover:bg-[#D85A30] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Retire
                        </button>
                      )}
                      {circle.state === 'retired' && (
                        <button
                          type="button"
                          onClick={() => openLifecycleAction(circle, 'draft')}
                          disabled={lifecycleSubmitting}
                          className="rounded-lg border border-[#085041] px-3 py-2 text-sm font-semibold text-[#085041] hover:bg-[#085041] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Restore draft
                        </button>
                      )}
                    </div>
                  </div>
                  {preflight && (
                    <div role="status" className={`mt-4 rounded-lg border px-3 py-2 text-sm ${preflight.stage_ready ? 'border-green-200 bg-green-50 text-green-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                      <p className="font-semibold">Publication staging: {preflight.stage_ready ? 'Ready' : 'Not ready'}</p>
                      <p className="mt-1">
                        Global release gate: {preflight.release_gate_enabled ? 'On' : 'Off'}.
                        {!preflight.release_gate_enabled && ' Publishing stages this Circle but does not make it public.'}
                      </p>
                      {preflight.missing_requirements.length > 0 && (
                        <p className="mt-1">Missing requirements: {preflight.missing_requirements.join(', ')}</p>
                      )}
                    </div>
                  )}
                  {circle.state === 'published' && stagedPublication[circle.club_id] && (
                    <div role="status" className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                      <p className="font-semibold">Publication staged safely.</p>
                      <p className="mt-1">
                        State version {stagedPublication[circle.club_id].state_version}, profile version {stagedPublication[circle.club_id].profile_version}.
                        {!stagedPublication[circle.club_id].release_gate_enabled && ' The global release gate is off; this Circle is not public.'}
                      </p>
                    </div>
                  )}
                </article>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{circleTotal} Circle{circleTotal === 1 ? '' : 's'}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous Official Circle page"
                disabled={circlePage <= 1 || loadingCircles || lifecycleSubmitting}
                onClick={() => setCirclePage((value) => value - 1)}
                className="rounded border border-gray-200 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span>Page {circlePage} of {circleTotalPages}</span>
              <button
                type="button"
                aria-label="Next Official Circle page"
                disabled={circlePage >= circleTotalPages || loadingCircles || lifecycleSubmitting}
                onClick={() => setCirclePage((value) => value + 1)}
                className="rounded border border-gray-200 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-7">
          <fieldset disabled={submitting}>
            <legend className="text-lg font-semibold text-gray-800">1. Choose the canonical identity type</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {(['author', 'publisher'] as OfficialType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => changeType(type)}
                  className={`rounded-lg border px-4 py-3 text-left transition-colors ${officialType === type
                    ? 'border-[#085041] bg-[#085041]/5 text-[#085041]'
                    : 'border-gray-200 text-gray-600 hover:border-[#085041]/50'
                  }`}
                  aria-pressed={officialType === type}
                >
                  <span className="block font-semibold">{type === 'author' ? 'Author Circle' : 'Publisher Circle'}</span>
                  <span className="block text-xs mt-1">Select an existing verified canonical record.</span>
                </button>
              ))}
            </div>
          </fieldset>

          <section>
            <label htmlFor="official-circle-search" className="text-lg font-semibold text-gray-800 block">
              2. Select an eligible {officialType}
            </label>
            <input
              id="official-circle-search"
              type="search"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder={`Search canonical ${officialType} names`}
              className="mt-4 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">Choose a returned record. Typed text alone can never be submitted.</p>

            <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
              {loadingCandidates && <p className="px-4 py-5 text-sm text-gray-500">Loading eligible canonical subjects…</p>}
              {!loadingCandidates && candidateError && (
                <p className="px-4 py-5 text-sm text-red-700 bg-red-50">{candidateError}</p>
              )}
              {!loadingCandidates && !candidateError && candidates.length === 0 && (
                <p className="px-4 py-5 text-sm text-gray-500">No eligible unbound {officialType}s found.</p>
              )}
              {!loadingCandidates && !candidateError && candidates.length > 0 && (
                <div className="divide-y divide-gray-100">
                  {candidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      onClick={() => {
                        setSelected(candidate);
                        setActionError(null);
                        setResult(null);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-[#085041]/5 ${selected?.id === candidate.id ? 'bg-[#085041]/10' : 'bg-white'}`}
                      aria-pressed={selected?.id === candidate.id}
                    >
                      <span className="block font-medium text-gray-800">{candidate.display_name}</span>
                      {candidate.name_sinhala && (
                        <span className="block text-sm text-gray-500 mt-0.5">{candidate.name_sinhala}</span>
                      )}
                      <span className="block text-xs text-gray-400 mt-1">ID: {candidate.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
              <span>{total} eligible result{total === 1 ? '' : 's'}</span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1 || submitting} onClick={() => setPage((value) => value - 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" disabled={page >= totalPages || submitting} onClick={() => setPage((value) => value + 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
              </div>
            </div>
          </section>

          <section>
            <label htmlFor="official-circle-reason" className="text-lg font-semibold text-gray-800 block">3. Record the provisioning reason</label>
            <textarea
              id="official-circle-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value.slice(0, MAX_REASON_LENGTH))}
              maxLength={MAX_REASON_LENGTH}
              rows={4}
              placeholder="Required audit reason"
              className="mt-4 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2 text-right">{reason.length}/{MAX_REASON_LENGTH}</p>
          </section>

          <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>I understand this creates one private, inactive draft Circle with zero members and no host grant.</span>
          </label>

          {actionError && <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">{actionError}</div>}
          {result && (
            <div role="status" className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 space-y-1">
              <p className="font-semibold">Draft Official Circle created.</p>
              <p>Circle ID: {result.club_id}</p>
              <p>Subject: {result.subject_id} ({result.official_type})</p>
              <p>State: {result.state}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-[#085041] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#063d32] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? 'Provisioning…' : 'Create draft Official Circle'}
          </button>
        </form>

        {profileCircle && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="official-circle-profile-heading"
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/40 p-4"
          >
            <section
              data-testid="official-circle-profile-modal"
              className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white p-6 shadow-xl"
            >
              <div className="flex shrink-0 items-start justify-between gap-4">
                <div>
                  <h2 id="official-circle-profile-heading" className="text-lg font-semibold text-gray-800">
                    Official Circle profile
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">{profileCircle.display_name}</p>
                </div>
                <button
                  type="button"
                  onClick={closeProfile}
                  disabled={profileSubmitting}
                  className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:opacity-50"
                >
                  Close
                </button>
              </div>

              <div
                data-testid="official-circle-profile-scroll-region"
                aria-label="Official Circle profile content"
                className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
              >
                {profileLoading && (
                  <p className="rounded-lg border border-gray-200 px-4 py-5 text-sm text-gray-500">
                    Loading profile…
                  </p>
                )}
                {!profileLoading && profileError && (
                  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {profileError}
                  </div>
                )}
                {!profileLoading && !profileError && profile && (
                  <>
                  <form onSubmit={handleProfileSave} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
                      <div><span className="text-gray-500">Profile version: </span><span className="text-gray-800">{profile.profile_version ?? 'Not created'}</span></div>
                      <div><span className="text-gray-500">Approval: </span><span className="text-gray-800">{profile.approval_status}</span></div>
                      <div><span className="text-gray-500">Approved at: </span><span className="text-gray-800">{profile.approved_at || 'Not approved'}</span></div>
                      <div><span className="text-gray-500">Ready: </span><span className="text-gray-800">{profile.ready ? 'Yes' : 'No'}</span></div>
                    </div>

                    {profilePublished && (
                      <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        This profile is immutable while the Circle is published. Pause the Circle before editing, approving, or revoking approval.
                      </p>
                    )}

                    {!profile.ready && profile.missing_requirements.length > 0 && (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        Missing requirements: {profile.missing_requirements.join(', ')}
                      </p>
                    )}

                    <label htmlFor="official-circle-profile-introduction" className="block text-sm font-semibold text-gray-800">
                      Public introduction
                    </label>
                    <textarea
                      id="official-circle-profile-introduction"
                      value={profileIntroduction}
                      onChange={(event) => {
                        setProfileIntroduction(event.target.value.slice(0, 2000));
                        setProfileError(null);
                      }}
                      maxLength={2000}
                      rows={7}
                      required
                      disabled={profileSubmitting || profilePublished}
                      placeholder="Required public introduction"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20 disabled:bg-gray-100"
                    />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-gray-500">{profileIntroduction.length}/2000</span>
                      <button
                        type="submit"
                        disabled={!profileIntroduction.trim() || profilePublished || profileSubmitting}
                        className="rounded-lg bg-[#085041] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {profileSubmitting ? 'Saving…' : profile.profile_exists ? 'Save profile' : 'Create profile'}
                      </button>
                    </div>
                  </form>

                  {profile.profile_exists && profile.approval_status === 'not_approved' && !profilePublished && (
                    <>
                      {profileIntroductionDirty && (
                        <p role="status" className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                          You have unsaved introduction changes. Save profile before approving.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => openProfileApproval('approve')}
                        disabled={profileSubmitting || !profileIntroduction.trim() || profileIntroductionDirty}
                        className="mt-5 rounded-lg border border-[#085041] px-4 py-2 text-sm font-semibold text-[#085041] hover:bg-[#085041] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Approve profile
                      </button>
                    </>
                  )}
                  {profile.profile_exists && profile.approval_status === 'approved' && !profilePublished && (
                    <button
                      type="button"
                      onClick={() => openProfileApproval('revoke')}
                      disabled={profileSubmitting}
                      className="mt-5 rounded-lg border border-[#D85A30] px-4 py-2 text-sm font-semibold text-[#D85A30] hover:bg-[#D85A30] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Revoke approval
                    </button>
                  )}

                  {profileAction && (
                    <form onSubmit={handleProfileApproval} className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                      <p className="text-sm font-semibold text-gray-800">
                        Confirm {profileAction === 'approve' ? 'profile approval' : 'approval revocation'}
                      </p>
                      <label htmlFor="official-circle-profile-reason" className="block text-sm font-semibold text-gray-800">
                        Reason
                      </label>
                      <textarea
                        id="official-circle-profile-reason"
                        value={profileReason}
                        onChange={(event) => {
                          setProfileReason(event.target.value.slice(0, MAX_REASON_LENGTH));
                          setProfileError(null);
                        }}
                        maxLength={MAX_REASON_LENGTH}
                        rows={3}
                        required
                        disabled={profileSubmitting}
                        placeholder="Required approval or revocation reason"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20 disabled:bg-gray-100"
                      />
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={profileActionConfirmed}
                          onChange={(event) => setProfileActionConfirmed(event.target.checked)}
                          disabled={profileSubmitting}
                          className="mt-0.5"
                        />
                        <span>I confirm this explicit profile approval action.</span>
                      </label>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setProfileAction(null)}
                          disabled={profileSubmitting}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={!profileReason.trim() || !profileActionConfirmed || profileSubmitting}
                          className="rounded-lg bg-[#085041] px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {profileSubmitting ? 'Saving…' : `Confirm ${profileAction}`}
                        </button>
                      </div>
                    </form>
                  )}
                  </>
                )}
              </div>
            </section>
          </div>
        )}

        {lifecycleAction && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="official-circle-lifecycle-heading"
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/40 p-4"
          >
            <form
              onSubmit={handleLifecycleSubmit}
              data-testid="official-circle-lifecycle-modal"
              className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white p-6 shadow-xl"
            >
              <h2 id="official-circle-lifecycle-heading" className="shrink-0 text-lg font-semibold text-gray-800">
                Confirm lifecycle change
              </h2>
              <div
                data-testid="official-circle-lifecycle-scroll-region"
                aria-label="Official Circle lifecycle confirmation content"
                className="mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
              >
                <p className="text-sm text-gray-600">
                  Confirm changing {lifecycleAction.circle.display_name} from {lifecycleAction.circle.state} to{' '}
                  {lifecycleAction.targetState}.
                </p>
                {lifecycleAction.targetState === 'published' ? (
                  <p className="mt-2 text-sm text-gray-600">
                    This stages the Circle as published after final server-side checks. The global release gate remains {lifecycleAction.preflight?.release_gate_enabled ? 'on' : 'off'}, so this does not make the Circle public while the gate is off.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    This changes only the lifecycle state. No members, hosts, claims, or public access are added.
                  </p>
                )}

                <label htmlFor="official-circle-lifecycle-reason" className="mt-5 block text-sm font-semibold text-gray-800">
                  Lifecycle reason
                </label>
                <textarea
                  id="official-circle-lifecycle-reason"
                  value={lifecycleReason}
                  onChange={(event) => {
                    setLifecycleReason(event.target.value.slice(0, MAX_REASON_LENGTH));
                    setLifecycleError(null);
                  }}
                  maxLength={MAX_REASON_LENGTH}
                  rows={4}
                  required
                  disabled={lifecycleSubmitting}
                  placeholder="Required internal reason"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20 disabled:bg-gray-100"
                />
                <p className="mt-2 text-right text-xs text-gray-500">
                  {lifecycleReason.length}/{MAX_REASON_LENGTH}
                </p>

                {lifecycleError && (
                  <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {lifecycleError}
                  </div>
                )}
              </div>

              <div className="mt-6 flex shrink-0 justify-end gap-3">
                <button
                  type="button"
                  onClick={closeLifecycleAction}
                  disabled={lifecycleSubmitting}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitLifecycle}
                  className="rounded-lg bg-[#085041] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {lifecycleSubmitting
                    ? 'Saving…'
                    : `Confirm ${lifecycleAction.targetState === 'published'
                      ? 'publish'
                      : lifecycleAction.targetState === 'paused'
                        ? 'pause'
                        : lifecycleAction.targetState === 'retired'
                          ? 'retire'
                          : 'restore draft'}`}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficialCircles;
